import { useEffect, useState } from 'react';
import { Box, Button, Skeleton, Typography } from '@compass/core';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { Column } from 'devextreme-react/data-grid';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DevExpressReport } from 'components';
import { GPASubmissionFilter } from './types';
import ApiService from 'services/apiService';
import { Permission, PermissionAction } from 'generated/openApi';
import { isAuthorized } from 'services/commonService';
import { studentListPageRoute } from 'pages/students/students/StudentListPage/StudentListPage';
import { studentPageRoute } from 'pages/students/students/StudentPage/StudentPage';

interface GPARawDataRow {
    gpaId: number;
    studentId: number;
    studentCustomId: string;
    studentName: string;
    firstName: string;
    lastName: string;
    year: number;
    schoolName: string;
    gradeLevelName: string;
    gpa: number | null;
    termTypeName: string;
    termNumber: number | null;
    fileId: number | null;
}

const renderSortableHeader = ({ column }: { column: { caption?: string; sortOrder?: string } }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', '&:hover .gpa-sort-arrows': { transform: 'scale(1.2)' } }}>
        {column.caption}
        <Box className="gpa-sort-arrows" sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', width: 22,
            alignSelf: 'stretch', transform: column.sortOrder ? 'scale(1.2)' : 'scale(1)', transition: 'transform 50ms ease-in-out' }}>
            <ArrowDropUpIcon color={column.sortOrder === 'asc' ? 'success' : undefined}
                sx={{ position: 'absolute', fontSize: 26, height: '100%', top: '-3px', opacity: column.sortOrder === 'asc' ? 1 : 0.2 }} />
            <ArrowDropDownIcon color={column.sortOrder === 'desc' ? 'success' : undefined}
                sx={{ position: 'absolute', fontSize: 26, height: '100%', top: '3px', opacity: column.sortOrder === 'desc' ? 1 : 0.2 }} />
        </Box>
    </Box>
);

export const GPARawDataDialog = ({ filter, onClose }: { filter: GPASubmissionFilter; onClose: () => void }) => {
    const [data, setData] = useState<GPARawDataRow[]>([]);
    const [studentCustomIdName, setStudentCustomIdName] = useState<string>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState<GPARawDataRow | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const canViewStudent = isAuthorized(Permission.Student, PermissionAction.View);

    const closeStudent = () => {
        setSelectedStudent(null);
        setRefreshKey(previous => previous + 1);
    };

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);
        Promise.all([
            ApiService.instance.post<GPARawDataRow[]>('/GPADashboard/GetGPARawData', filter),
            ApiService.students.getCustomIdTypes()
        ])
            .then(([response, customIdTypes]) => {
                if (!active) return;
                const primaryCustomIdType = customIdTypes.find(type => type.id === 1);
                if (!primaryCustomIdType) {
                    setError(true);
                    return;
                }
                setData(response.data);
                setStudentCustomIdName(primaryCustomIdType.name);
            })
            .catch(() => { if (active) setError(true); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [filter, refreshKey]);

    return <>
    <Dialog open onClose={onClose} maxWidth={false} fullWidth aria-labelledby="gpa-raw-data-title"
        PaperProps={{ sx: { width: '90vw', maxWidth: 'none', maxHeight: '85vh', borderRadius: 3 } }}>
        <DialogTitle id="gpa-raw-data-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
            GPA Raw Data
            <Button onClick={onClose} variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>Close</Button>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mt: 2, '& .dx-header-row .gpa-sort-header .dx-sort': { display: 'none' } }}>
                {/* <Typography sx={{ mb: 2 }}>Individual GPA records for the selected GPA table filters.</Typography> */}
                {loading ? <Skeleton variant="rounded" height={400} /> : error ?
                    <Typography color="error" role="alert">Failed to load GPA raw data. Close and reopen this window to try again.</Typography> :
                    <DevExpressReport dataSource={data} showColumnChooser={false} showSearchPanel>
                        <Column dataField="studentCustomId" caption={studentCustomIdName} dataType="string" allowSorting cssClass="gpa-sort-header" headerCellRender={renderSortableHeader} />
                        <Column dataField="lastName" caption="Last Name" dataType="string" allowSorting cssClass="gpa-sort-header" headerCellRender={renderSortableHeader} />
                        <Column dataField="firstName" caption="First Name" dataType="string" allowSorting cssClass="gpa-sort-header" headerCellRender={renderSortableHeader} />
                        <Column dataField="year" caption="School Year" cellRender={({ value }) => `${value}-${value + 1}`} />
                        <Column dataField="schoolName" caption="School" />
                        <Column dataField="gradeLevelName" caption="Grade Level" />
                        <Column dataField="gpa" caption="GPA" dataType="number" />
                        <Column dataField="termTypeName" caption="Term Type" />
                        <Column dataField="termNumber" caption="Term Number" />
                        <Column dataField="fileId" caption="GPA File ID" visible={data.some(row => row.fileId != null)} />
                        <Column caption="View Student" visible={canViewStudent} allowSorting={false} allowFiltering={false}
                            cellRender={({ data: row }: { data: GPARawDataRow }) =>
                                <Button size="small" onClick={() => setSelectedStudent(row)}>View Student</Button>} />
                    </DevExpressReport>}
            </Box>
        </DialogContent>
    </Dialog>
    {selectedStudent && <Dialog open onClose={closeStudent} maxWidth={false} fullWidth aria-labelledby="gpa-student-profile-title"
        PaperProps={{ sx: { width: '95vw', maxWidth: 'none', height: '90vh', maxHeight: '90vh', borderRadius: 3 } }}>
        <DialogTitle id="gpa-student-profile-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
            Student Profile - {selectedStudent.studentName}
            <Button onClick={closeStudent} variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>Close</Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
            <iframe title={`Student profile for ${selectedStudent.studentName}`}
                src={`/${studentListPageRoute.getPath()}/${studentPageRoute.getPath({ id: selectedStudent.studentId, crudMode: 'view' })}`}
                style={{ width: '100%', height: '100%', border: 'none' }} />
        </DialogContent>
    </Dialog>}
    </>;
};
